import { makeStyles, shorthands } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

export const FAILED_UPLOAD_AUTO_DISMISS_DELAY_MS = 15000;
export const ERROR_BANNER_AUTO_DISMISS_DELAY_MS = 10000;
export const BREADCRUMB_SEGMENT_MAX_WIDTH_PX = 200;

export const useStyles = makeStyles({
  pageWrapper: {
    width: "100%",
    paddingTop: designTokens.spacing[5],
  },
  contentColumn: {
    width: "100%",
    gap: designTokens.spacing[8],
    display: "flex",
    flexDirection: "column",
  },
  hiddenFileInput: {
    display: "none",
  },
  sectionHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    paddingBottom: designTokens.spacing[3],
  },
  root: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[10],
    height: "100%",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: projectIqTokens.fontFamily,
  },
  headerTitle: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },
  uploadButton: {
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    border: `1px solid ${projectIqTokens.line}`,
    fontWeight: designTokens.fontWeight.medium,
    backgroundColor: projectIqTokens.card,
    fontSize: "13px",
    color: projectIqTokens.ink,
    fontFamily: projectIqTokens.fontFamily,
    height: "36px",
    padding: "0 13px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 0,
    "--fui-Button__icon--spacing": "7px",
    ":hover": {
      backgroundColor: projectIqTokens.bg,
    },
    ":active": {
      backgroundColor: projectIqTokens.bg2,
    },
  },
  fileListCard: {
    borderRadius: "14px",
    border: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    boxShadow: projectIqTokens.shadowSm,
    ...shorthands.overflow("hidden"),
  },
  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  skeletonCheckboxSpacer: {
    width: "34px",
    flexShrink: 0,
  },
  skeletonIcon: {
    width: "34px",
    height: "34px",
    flexShrink: 0,
    borderRadius: designTokens.borderRadius.md,
  },
  skeletonNameCell: {
    flex: 1,
    minWidth: 0,
  },
  skeletonName: {
    width: "50%",
    height: "14px",
    borderRadius: designTokens.borderRadius.sm,
  },
  skeletonTypeCell: {
    width: "200px",
    flexShrink: 0,
  },
  skeletonTypeTag: {
    width: "84px",
    height: "22px",
    borderRadius: "999px",
  },
  skeletonFilterPill: {
    height: "32px",
    borderRadius: designTokens.borderRadius.full,
  },
  skeletonTrailingSpacer: {
    width: "212px",
    flexShrink: 0,
  },
  fileRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  fileRowDisabled: {
    opacity: 0.5,
    pointerEvents: "none" as const,
  },
  fileRowSelected: {
    backgroundColor: projectIqTokens.bg,
  },
  fileIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    height: "34px",
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    flexShrink: 0,
  },
  fileIconPdf: {
    backgroundColor: "rgba(194,85,63,0.12)",
    color: projectIqTokens.rust,
  },
  fileIconXls: {
    backgroundColor: "rgba(47,158,110,0.12)",
    color: projectIqTokens.green,
  },
  fileIconPpt: {
    backgroundColor: "rgba(217,138,43,0.12)",
    color: projectIqTokens.amber,
  },
  fileIconDefault: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.ink3,
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: "13.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink2,
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
  },
  fileMeta: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink3,
  },
  menuButton: {
    color: projectIqTokens.faint,
    minWidth: "auto",
    flexShrink: 0,
  },
  menuItemDanger: {
    "&&": {
      color: projectIqTokens.rust,
    },
    "&&:hover": {
      color: projectIqTokens.rust,
    },
    "&&:hover:active": {
      color: projectIqTokens.rust,
    },
    "&& .fui-MenuItem__icon": {
      color: projectIqTokens.rust,
    },
    "&&:hover .fui-MenuItem__icon": {
      color: projectIqTokens.rust,
    },
    "&&:hover:active .fui-MenuItem__icon": {
      color: projectIqTokens.rust,
    },
  },
  columnHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    backgroundColor: projectIqTokens.bg2,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
  },
  compactHeaderCheckbox: {
    "&& .fui-Checkbox__indicator": {
      marginTop: designTokens.spacing[1],
      marginBottom: designTokens.spacing[1],
    },
  },
  fileNameColumnHeader: {
    flex: 1,
    minWidth: 0,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },
  typeColumnHeader: {
    width: "200px",
    flexShrink: 0,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },
  shareColumnHeader: {
    width: "168px",
    flexShrink: 0,
    textAlign: "right",
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },
  menuColumnHeader: {
    width: "32px",
    flexShrink: 0,
  },
  typeTagCell: {
    display: "flex",
    alignItems: "center",
    width: "200px",
    flexShrink: 0,
  },
  shareToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "168px",
    flexShrink: 0,
  },
  shareToggleSwitch: {
    "&& input:not(:checked) ~ .fui-Switch__indicator": {
      color: projectIqTokens.card,
    },
    "&& input:not(:checked):hover ~ .fui-Switch__indicator": {
      color: projectIqTokens.card,
    },
  },
  selectionCheckbox: {
    "&& input:not(:checked) ~ .fui-Checkbox__indicator": {
      backgroundColor: projectIqTokens.card,
    },
    "&& input:not(:checked):hover ~ .fui-Checkbox__indicator": {
      backgroundColor: projectIqTokens.card,
    },
  },
  typeFilterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
    padding: `0 ${designTokens.spacing[4]} ${designTokens.spacing[3]}`,
  },
  typeFilterButtonLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[3],
    paddingTop: designTokens.spacing[8],
    paddingBottom: designTokens.spacing[8],
    color: projectIqTokens.muted,
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    backgroundColor: "rgba(194,85,63,0.12)",
    color: projectIqTokens.rust,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
  },
  errorBoxMessage: {
    color: "inherit",
    fontSize: "inherit",
  },
  errorBoxDismissButton: {
    flexShrink: 0,
    color: projectIqTokens.rust,
    minWidth: "auto",
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.tealLine}`,
    borderRadius: designTokens.borderRadius.md,
    color: projectIqTokens.tealInk,
    fontSize: designTokens.fontSize.sm,
  },
  infoBannerIcon: {
    color: projectIqTokens.teal,
    flexShrink: 0,
  },
  indexingBanner: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.tealLine}`,
    borderRadius: designTokens.borderRadius.md,
    color: projectIqTokens.ink2,
    fontSize: designTokens.fontSize.sm,
  },
  dropzoneWrapper: {
    display: "none",
  },
  fileSpinner: {
    flexShrink: 0,
  },
  failedUploadCountdownTrack: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "2px",
    backgroundColor: projectIqTokens.line2,
    ...shorthands.overflow("hidden"),
  },
  failedUploadCountdownFill: {
    height: "100%",
    backgroundColor: projectIqTokens.muted,
    animationDuration: `${FAILED_UPLOAD_AUTO_DISMISS_DELAY_MS}ms`,
    animationTimingFunction: "linear",
    animationFillMode: "forwards",
    animationName: {
      from: { width: "100%" },
      to: { width: "0%" },
    },
  },
  sharepointCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderRadius: "14px",
    background: `linear-gradient(135deg, ${projectIqTokens.navy} 0%, ${projectIqTokens.navy2} 100%)`,
  },
  sharepointIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: designTokens.neutral.white,
    flexShrink: 0,
  },
  sharepointInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  sharepointTitle: {
    fontSize: "13.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral.white,
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
  },
  sharepointMeta: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
  },
  sharepointBadge: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexShrink: 0,
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.green,
  },
  sharepointBadgeDot: {
    width: "8px",
    height: "8px",
    ...shorthands.borderRadius(designTokens.borderRadius.full),
    backgroundColor: projectIqTokens.green,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[8]}`,
    backgroundColor: projectIqTokens.card,
    borderBottom: `1px solid ${projectIqTokens.line}`,
    fontFamily: projectIqTokens.fontFamily,
  },
  pageHeaderInfo: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  pageHeaderSubtitle: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.teal,
    fontWeight: designTokens.fontWeight.medium,
  },
  pageHeaderTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
  },
  connectSharePointButton: {
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: projectIqTokens.teal,
    color: designTokens.neutral.white,
    fontWeight: designTokens.fontWeight.bold,
    fontSize: designTokens.fontSize.sm,
    fontFamily: projectIqTokens.fontFamily,
    padding: designTokens.spacing[2],
    ":hover": {
      backgroundColor: projectIqTokens.tealInk,
      color: designTokens.neutral.white,
    },
    ":active": {
      backgroundColor: projectIqTokens.tealInk,
      color: designTokens.neutral.white,
    },
  },
  breadcrumbRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    flex: 1,
    minWidth: 0,
    flexWrap: "nowrap",
    fontSize: "13px",
    ...shorthands.overflow("hidden"),
  },
  breadcrumbBack: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    height: "auto",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: projectIqTokens.faint,
    padding: 0,
    flexShrink: 0,
    ":hover": {
      color: projectIqTokens.muted,
    },
  },
  breadcrumbContainer: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    minWidth: 0,
    flex: 1,
    flexWrap: "nowrap",
  },
  breadcrumbSegmentWrapper: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flexShrink: 1,
  },
  breadcrumbSegment: {
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: projectIqTokens.ink,
    fontSize: "13px",
    fontWeight: designTokens.fontWeight.bold,
    fontFamily: projectIqTokens.fontFamily,
    maxWidth: `${BREADCRUMB_SEGMENT_MAX_WIDTH_PX}px`,
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    ":hover": {
      color: projectIqTokens.ink2,
    },
  },
  breadcrumbSegmentAncestor: {
    color: projectIqTokens.muted,
    fontWeight: designTokens.fontWeight.medium,
  },
  breadcrumbSegmentRoot: {
    flexShrink: 0,
  },
  breadcrumbSeparator: {
    color: projectIqTokens.faint,
    fontSize: "12px",
    fontWeight: designTokens.fontWeight.medium,
    marginRight: designTokens.spacing[2],
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  folderRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
    backgroundColor: projectIqTokens.card,
    cursor: "pointer",
    transition: `background-color 150ms ease, opacity 150ms ease`,
    ":hover": {
      backgroundColor: projectIqTokens.bg,
    },
  },
  dropTarget: {
    backgroundColor: projectIqTokens.tealSoft,
    outline: `2px dashed ${projectIqTokens.teal}`,
    outlineOffset: "-2px",
    ":hover": {
      backgroundColor: projectIqTokens.tealSoft,
    },
  },
  folderRowDisabled: {
    opacity: 0.5,
    pointerEvents: "none" as const,
    ":hover": {
      backgroundColor: projectIqTokens.card,
    },
  },
  folderIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    height: "34px",
    backgroundColor: "rgba(217, 138, 43, 0.12)",
    color: projectIqTokens.amber,
    flexShrink: 0,
    ...shorthands.borderRadius(designTokens.borderRadius.md),
  },
  folderInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  folderName: {
    fontSize: "13.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
    "[data-folder-row]:hover &": {
      textDecoration: "underline",
    },
  },
  folderFileCount: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
  },
  folderUploadBatchProgressText: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink3,
  },
  newFolderRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderBottom: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    borderRadius: designTokens.borderRadius.lg,
    marginBottom: designTokens.spacing[4],
    boxShadow: projectIqTokens.shadowSm,
  },
  newFolderIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    height: "34px",
    color: projectIqTokens.amber,
    flexShrink: 0,
  },
  newFolderInput: {
    flex: 1,
    height: "32px",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.base,
    fontFamily: projectIqTokens.fontFamily,
    color: projectIqTokens.ink,
    ":focus": {
      outline: "none",
      border: `1px solid ${projectIqTokens.teal}`,
      boxShadow: `0 0 0 2px ${projectIqTokens.tealSoft}`,
    },
    "::placeholder": {
      color: projectIqTokens.faint,
    },
  },
  newFolderCreateButton: {
    height: "32px",
    padding: `0 ${designTokens.spacing[3]}`,
    backgroundColor: projectIqTokens.teal,
    color: designTokens.neutral.white,
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    fontFamily: projectIqTokens.fontFamily,
    ":hover": {
      backgroundColor: projectIqTokens.tealInk,
    },
    ":active": {
      backgroundColor: projectIqTokens.tealInk,
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  newFolderCancelButton: {
    height: "32px",
    padding: `0 ${designTokens.spacing[3]}`,
    backgroundColor: "transparent",
    color: projectIqTokens.ink2,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    fontFamily: projectIqTokens.fontFamily,
    ":hover": {
      backgroundColor: projectIqTokens.bg,
    },
    ":active": {
      backgroundColor: projectIqTokens.bg2,
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  emptyFolderState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[3],
    paddingTop: designTokens.spacing[8],
    paddingBottom: designTokens.spacing[8],
    color: projectIqTokens.muted,
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: designTokens.spacing[3],
    gap: designTokens.spacing[4],
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  checkboxWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    flexShrink: 0,
  },
  checkboxColumnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    flexShrink: 0,
  },
  bulkActionBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.tealLine}`,
    borderRadius: designTokens.borderRadius.lg,
    marginBottom: designTokens.spacing[3],
    gap: designTokens.spacing[2],
  },
  bulkActionBarLeft: {
    display: "flex",
    alignItems: "center",
    flex: 1,
  },
  bulkActionBarSelectionText: {
    fontSize: "13px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.tealInk,
    whiteSpace: "nowrap",
  },
  bulkActionBarActions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  bulkActionButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    height: "30px",
    padding: "0 11px",
    border: `1px solid ${projectIqTokens.tealLine}`,
    backgroundColor: projectIqTokens.card,
    borderRadius: designTokens.borderRadius.md,
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink2,
    fontFamily: projectIqTokens.fontFamily,
    cursor: "pointer",
    ":hover": {
      backgroundColor: projectIqTokens.bg,
    },
    ":active": {
      backgroundColor: projectIqTokens.bg2,
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  bulkActionButtonDanger: {
    color: projectIqTokens.rust,
  },
  bulkActionClearButton: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    ":hover": {
      color: projectIqTokens.ink,
      textDecoration: "underline",
    },
  },
  folderNavigateButton: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    flex: 1,
    minWidth: 0,
    padding: 0,
    textAlign: "left",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "transparent",
    },
    ":active": {
      backgroundColor: "transparent",
    },
  },
  folderUploadBatchRow: {
    flexDirection: "column",
    alignItems: "stretch",
    cursor: "default",
    ":hover": {
      backgroundColor: projectIqTokens.card,
    },
  },
  folderUploadBatchSummary: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    width: "100%",
  },
  folderUploadFailureList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    marginTop: designTokens.spacing[3],
    marginLeft: "46px",
    padding: designTokens.spacing[3],
    backgroundColor: projectIqTokens.rustBg,
    border: `1px solid ${projectIqTokens.line2}`,
    borderRadius: designTokens.borderRadius.md,
  },
  folderUploadFailureListHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
  },
  folderUploadFailureListTitle: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.rust,
  },
  folderUploadFailureIconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    color: projectIqTokens.ink3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: projectIqTokens.bg,
      color: projectIqTokens.ink2,
    },
  },
  folderUploadFailureListItems: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  folderUploadFailureListItem: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink3,
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
  },
});
